// Layer 4 (UI) — the comp's slim range control, shared by the feed-ore and
// metal-price strips. A native range input sits over the drawn track for
// keyboard and pointer control; the track, fill, knob and optional tick are
// drawn from the transform's percentages.

import { cn } from '@/shared/cn';

export type CapacityRangeProps = {
  label: string;
  /** Accessible name of the input; the visible label is the default. */
  inputLabel?: string;
  display: string;
  min: number;
  max: number;
  step: number;
  value: number;
  minLabel: string;
  maxLabel: string;
  fillPct: number;
  /** A what-if in force: the fill and knob take the accent (A9). */
  moved: boolean;
  markPct?: number;
  markLabel?: string;
  onChange: (value: number) => void;
};

export function CapacityRange({
  label,
  inputLabel,
  display,
  min,
  max,
  step,
  value,
  minLabel,
  maxLabel,
  fillPct,
  moved,
  markPct,
  markLabel,
  onChange,
}: CapacityRangeProps) {
  return (
    <label className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-1">
      {/* K18 r1: gap-x-1 (not gap-x-2) so the wider `US$/dmt` unit keeps the
          label whole in its column at 1470.
          F16: below 1400 the price columns are narrower than `Zinc (Zn) ·
          US$/dmt` and truncation cut the label's unit off. `truncate` now holds
          only at ≥1400 (where the grid has its 1470 tracks), so narrower
          layouts let the label wrap whole instead of ellipsing. */}
      <span data-capacity-range-label className="text-xs font-medium text-ink-primary min-[1400px]:truncate">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-ink-primary">{display}</span>
      <span className="relative col-span-2 mb-[3px] mt-[7px] block h-1 rounded bg-[var(--hairline-strong)]">
        <span
          aria-hidden
          className={cn('absolute inset-y-0 left-0 rounded', moved ? 'bg-[var(--color-accent-500)]' : 'bg-[var(--ink-secondary)]')}
          style={{ width: `${fillPct}%` }}
        />
        {markPct !== undefined ? (
          <span
            aria-hidden
            className="absolute -top-[5px] h-3.5 w-0.5 bg-[var(--color-accent-500)]"
            style={{ left: `${markPct}%` }}
          />
        ) : null}
        <input
          aria-label={inputLabel ?? label}
          className="peer absolute -inset-x-1.5 -top-2 h-5 w-[calc(100%+0.75rem)] cursor-pointer opacity-0"
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.currentTarget.value))}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-[var(--surface-2)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent-500)]',
            moved ? 'border-[var(--color-accent-500)]' : 'border-[var(--ink-primary)]',
          )}
          style={{ left: `${fillPct}%` }}
        />
      </span>
      <span aria-hidden className="col-span-2 flex justify-between text-[10px] tabular-nums text-muted">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </span>
      {markLabel ? (
        <span className="col-span-2 text-right text-[10px] tabular-nums text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]">
          {markLabel}
        </span>
      ) : null}
    </label>
  );
}
